import { noop, type ThemeType } from '@capsicum/common';
import { InteractionLayer, Label } from '@capsicum/common-web';
import { AccessIcon } from '@capsicum/common-web/icons';
import { useTokenParticipationAmount } from '@capsicum/socios-core';
import { type SVGProps } from 'react';
import styled, { useTheme } from 'styled-components';

export type TokenParticipationAmountProps = {
	additionalText?: string;
	amount: number;
	onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	partnerId: string;
	suffix?: string;
	testID: string;
	variant?: 'default' | 'outlined';
};

/**
 * TODO: Migrate this component to crypto feature once it's been created
 */

/**
 * TokenParticipationAmount is the Fan Token Container indicating how many fan tokens is required if the
 * user does not own enough token(s) of the partner.
 *
 * @param {TokenParticipationAmountProps} props - The props of the container
 * @param {number} props.amount The required amount of fan token(s)
 * @param {string} props.partnerId The partnerId by which we can retrieve partner data
 * @param {string} props.testID the ID to enable e2e framework to grab this element
 * @param {() => void} props.onClick The callback function to be called on container click
 * @param {'default' | 'outlined'} props.variant Whether the container is outlined or with partner custom colors
 * @param {string=} props.suffix Simple text to be added right after the token shortname.
 * @param {string=} props.additionalText Simple text to be added as an additional text at the end of the container.
 * @returns {JSX.Element} The Fan Token Container
 */

export const TokenParticipationAmount = ({
	additionalText,
	amount,
	onClick = noop,
	partnerId,
	suffix,
	testID,
	variant = 'default',
	...props
}: TokenParticipationAmountProps) => {
	const theme = useTheme() as ThemeType;

	const { customColors, text } = useTokenParticipationAmount(amount, partnerId, theme, suffix);

	const customAccessIcon = (iconProps: SVGProps<SVGSVGElement>) => (
		<AccessIcon height={16} width={16} {...iconProps} />
	);

	return (
		<S.Wrapper data-testid={testID} {...props}>
			<InteractionLayer disabled={false} onClick={onClick}>
				<Label
					customColors={variant === 'default' ? customColors : undefined}
					icon={customAccessIcon}
					label={text}
					size="default"
					variant={variant}
				>
					{additionalText && <S.TextEl>{additionalText}</S.TextEl>}
				</Label>
			</InteractionLayer>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		${({ theme }) => theme.border.radius.circle};
		overflow: hidden;
		width: fit-content;
	`;

	export const TextEl = styled.span`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		${({ theme }) => theme.fonts.text.text4};
	`;
}
